import { useEffect, useState } from 'react';
import { post } from '../../../../api/client';

/** POST /payment/estimate 的返回（经 client 解包 data） */
export interface EstimateData {
  planCode: string | null;
  name: string;
  durationMonths: number;
  originalAmount: number;
  couponDiscount: number;
  promotionDiscount: number;
  referralDiscount: number;
  discountAmount: number;
  finalAmount: number;
  appliedType: 'coupon' | 'promotion' | 'referral' | null;
}

interface Params {
  /** 仅在结算弹窗打开且未下单时试算 */
  enabled: boolean;
  planCode?: string;
  planMonths?: number;
  isRenewal?: boolean;
  couponCode?: string;
  promotionCode?: string;
  referralCode?: string;
  /** 防抖，默认 500ms，避免逐字符请求 */
  debounceMs?: number;
}

/**
 * 下单前优惠试算：对优惠券/推广码/邀请码做服务端预校验，并实时返回减免与最终价。
 * 无任一优惠码时不请求（展示套餐原价）；优惠码无效时透出后端中文提示（error）。
 * 减免额由服务端权威计算，前端仅展示，杜绝客户端篡改。
 */
export function useDiscountEstimate({
  enabled,
  planCode,
  planMonths,
  isRenewal,
  couponCode,
  promotionCode,
  referralCode,
  debounceMs = 500,
}: Params) {
  const [data, setData] = useState<EstimateData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const c = couponCode?.trim() ?? '';
  const p = promotionCode?.trim() ?? '';
  const r = referralCode?.trim() ?? '';

  useEffect(() => {
    // 未启用或无任何优惠码 → 清空试算结果，展示原价
    if (!enabled || (!c && !p && !r)) {
      setData(null);
      setError(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        const body: Record<string, unknown> = { isRenewal: Boolean(isRenewal) };
        if (planCode) body.planCode = planCode;
        else if (planMonths != null) body.planMonths = planMonths;
        if (c) body.couponCode = c;
        if (p) body.promotionCode = p;
        if (r) body.referralCode = r;

        const res = await post<EstimateData>('/payment/estimate', body);
        if (!cancelled) {
          setData(res);
          setError(null);
        }
      } catch (e) {
        if (!cancelled) {
          setData(null);
          setError(e instanceof Error ? e.message : '优惠码校验失败');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, debounceMs);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [enabled, planCode, planMonths, isRenewal, c, p, r, debounceMs]);

  return { data, error, loading };
}
